import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DocumentMagnifyingGlassIcon,
  FolderOpenIcon,
  PlusIcon,
  XMarkIcon,
  ArrowRightIcon,
  FunnelIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ClockIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { discoverFOIADocumentsStream } from '../services/api';
import ProcessingStatus from '../components/ProcessingStatus';

export default function FOIADiscovery() {
  const navigate = useNavigate();
  const location = useLocation();

  // Form state
  const [foiaRequestText, setFoiaRequestText] = useState('');
  const [sourcePaths, setSourcePaths] = useState(['/app/data/test_foia_discovery']);
  const [newSourcePath, setNewSourcePath] = useState('');
  const [agency, setAgency] = useState('');
  const [requester, setRequester] = useState('');
  const [relevanceThreshold, setRelevanceThreshold] = useState(0.3);
  const [maxFileSizeMb, setMaxFileSizeMb] = useState(50);

  // S3 connector state
  const [s3AccessKeyId, setS3AccessKeyId] = useState('');
  const [s3SecretAccessKey, setS3SecretAccessKey] = useState('');
  const [s3Region, setS3Region] = useState('us-east-1');

  // M365 connector state
  const [m365TenantId, setM365TenantId] = useState('');
  const [m365ClientId, setM365ClientId] = useState('');
  const [m365ClientSecret, setM365ClientSecret] = useState('');

  // Results state
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [progress, setProgress] = useState(null);
  const [progressDetails, setProgressDetails] = useState(null);
  const [error, setError] = useState(null);
  const [streamController, setStreamController] = useState(null);

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('relevance');
  const [showM365Setup, setShowM365Setup] = useState(false);

  // Restore state from query history if navigating from history
  useEffect(() => {
    const restoredQuery = location.state?.restoredQuery;
    if (restoredQuery && restoredQuery.query_type === 'foia_discovery') {
      // Restore FOIA request text
      if (restoredQuery.input_text) {
        setFoiaRequestText(restoredQuery.input_text);
      }

      // Restore input parameters
      if (restoredQuery.input_params) {
        if (restoredQuery.input_params.source_paths) {
          setSourcePaths(restoredQuery.input_params.source_paths);
        }
        if (restoredQuery.input_params.agency) {
          setAgency(restoredQuery.input_params.agency);
        }
        if (restoredQuery.input_params.requester) {
          setRequester(restoredQuery.input_params.requester);
        }
        if (restoredQuery.input_params.relevance_threshold !== undefined) {
          setRelevanceThreshold(restoredQuery.input_params.relevance_threshold);
        }
        if (restoredQuery.input_params.max_file_size_mb) {
          setMaxFileSizeMb(restoredQuery.input_params.max_file_size_mb);
        }
      }

      // Restore full result including documents
      if (restoredQuery.result) {
        setResult(restoredQuery.result);
      }

      // Clear location state to prevent re-restoration on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const addSourcePath = () => {
    const path = newSourcePath.trim();
    if (path && !sourcePaths.includes(path)) {
      setSourcePaths([...sourcePaths, path]);
      setNewSourcePath('');
    }
  };

  const removeSourcePath = (index) => {
    setSourcePaths(sourcePaths.filter((_, i) => i !== index));
  };

  const handleDiscover = () => {
    if (!foiaRequestText.trim() || sourcePaths.length === 0) return;

    setIsLoading(true);
    setError(null);
    setResult(null);
    setProgress(0);
    setProgressDetails(null);
    setLoadingMessage('Starting FOIA discovery...');

    const stream = discoverFOIADocumentsStream(
      foiaRequestText,
      sourcePaths,
      {
        agency,
        requester,
        relevanceThreshold,
        maxFileSizeMb,
        s3AccessKeyId,
        s3SecretAccessKey,
        s3Region,
        m365TenantId,
        m365ClientId,
        m365ClientSecret
      },
      {
        onProgress: (data) => {
          setProgress(data.overall_progress);
          setLoadingMessage(data.message);
          setProgressDetails({
            phase: data.phase,
            phaseProgress: data.phase_progress,
            currentFile: data.current_file,
            filesProcessed: data.files_processed,
            totalFiles: data.total_files,
            responsiveFound: data.responsive_found,
          });
        },
        onComplete: (data) => {
          setResult(data);
          setIsLoading(false);
          setProgress(null);
          setProgressDetails(null);
          setLoadingMessage('');
          setStreamController(null);
        },
        onError: (err) => {
          setError(err.message || 'Discovery failed');
          setIsLoading(false);
          setProgress(null);
          setProgressDetails(null);
          setLoadingMessage('');
          setStreamController(null);
        },
      }
    );

    setStreamController(stream);
  };

  const handleCancelDiscovery = () => {
    if (streamController) {
      streamController.cancel();
      setStreamController(null);
      setIsLoading(false);
      setProgress(null);
      setProgressDetails(null);
      setLoadingMessage('');
    }
  };

  const sendToRedaction = (doc) => {
    navigate('/foia', {
      state: {
        discoveredDocument: {
          filePath: doc.file_path,
          filename: doc.filename,
          contentPreview: doc.content_preview,
        },
      },
    });
  };

  const sortedDocuments = result?.documents
    ? [...result.documents].sort((a, b) => {
        if (sortBy === 'relevance') return b.relevance_score - a.relevance_score;
        if (sortBy === 'name') return a.filename.localeCompare(b.filename);
        if (sortBy === 'size') return b.file_size - a.file_size;
        return 0;
      })
    : [];

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getRelevanceColor = (score) => {
    if (score >= 0.7) return 'text-green-700 bg-green-100';
    if (score >= 0.4) return 'text-yellow-700 bg-yellow-100';
    return 'text-gray-700 bg-gray-100';
  };

  const getActionBadge = (action) => {
    if (action === 'likely_responsive') return { label: 'Likely Responsive', color: 'bg-green-100 text-green-700' };
    if (action === 'review') return { label: 'Needs Review', color: 'bg-yellow-100 text-yellow-700' };
    return { label: 'Exclude', color: 'bg-gray-100 text-gray-600' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <DocumentMagnifyingGlassIcon className="w-8 h-8 mr-3 text-blue-600" />
          FOIA Document Discovery
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Scan data sources to identify documents responsive to a FOIA request
        </p>
      </div>

      {/* Processing Status */}
      {isLoading && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <ProcessingStatus
            isProcessing={isLoading}
            message={loadingMessage}
            progress={progress}
          />
          {progressDetails && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div className="text-center">
                  <div className="text-lg font-semibold text-blue-300">
                    {progressDetails.filesProcessed}/{progressDetails.totalFiles}
                  </div>
                  <div className="text-gray-500">Files Processed</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-green-300">
                    {progressDetails.responsiveFound}
                  </div>
                  <div className="text-gray-500">Responsive Found</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-purple-300 capitalize">
                    {progressDetails.phase}
                  </div>
                  <div className="text-gray-500">Current Phase</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-indigo-300">
                    {progressDetails.phaseProgress?.toFixed(0) || 0}%
                  </div>
                  <div className="text-gray-500">Phase Progress</div>
                </div>
              </div>
              {progressDetails.currentFile && (
                <div className="mt-2 text-center">
                  <span className="text-xs text-gray-400">Current file: </span>
                  <span className="text-xs font-mono text-gray-600 truncate">
                    {progressDetails.currentFile}
                  </span>
                </div>
              )}
              <div className="mt-3 text-center">
                <button
                  onClick={handleCancelDiscovery}
                  className="text-xs text-red-600 hover:text-red-800 underline"
                >
                  Cancel Discovery
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Input Form */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
        {/* FOIA Request Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            FOIA Request Text
          </label>
          <textarea
            value={foiaRequestText}
            onChange={(e) => setFoiaRequestText(e.target.value)}
            placeholder="Paste the FOIA request here. The system will analyze the request to identify subject matter, keywords, and date ranges for document discovery..."
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>

        {/* Data Sources */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FolderOpenIcon className="w-4 h-4 inline mr-1" />
            Data Sources
          </label>
          <div className="space-y-2">
            {sourcePaths.map((path, idx) => (
              <div key={idx} className="flex items-center space-x-2">
                <div className="flex-1 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700 font-mono">
                  {path}
                </div>
                <button
                  onClick={() => removeSourcePath(idx)}
                  className="p-1 text-gray-400 hover:text-red-500"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={newSourcePath}
                onChange={(e) => setNewSourcePath(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addSourcePath()}
                placeholder="/app/data/documents"
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
              />
              <button
                onClick={addSourcePath}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                Add
              </button>
            </div>
          </div>
          <p className="mt-1 text-xs text-gray-400">
            Paths are relative to the container filesystem. <code>/app/data/</code> maps to the local <code>data/</code> directory.
            <br/>
            For S3 buckets, use <code>s3://bucket-name/prefix</code> format.
            <br/>
            For M365/Email, use <code>m365://user@domain.com/Inbox</code> format.
          </p>
        </div>

        {/* S3 Configuration */}
        {sourcePaths.some(p => p.startsWith('s3://')) && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-3">
              <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
              </svg>
              <span className="text-sm font-medium text-amber-800">AWS S3 Credentials Required</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Access Key ID</label>
                <input
                  type="text"
                  value={s3AccessKeyId}
                  onChange={(e) => setS3AccessKeyId(e.target.value)}
                  placeholder="AKIA..."
                  className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Secret Access Key</label>
                <input
                  type="password"
                  value={s3SecretAccessKey}
                  onChange={(e) => setS3SecretAccessKey(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Region</label>
                <select
                  value={s3Region}
                  onChange={(e) => setS3Region(e.target.value)}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="us-east-1">US East (N. Virginia)</option>
                  <option value="us-east-2">US East (Ohio)</option>
                  <option value="us-west-1">US West (N. California)</option>
                  <option value="us-west-2">US West (Oregon)</option>
                  <option value="eu-west-1">EU (Ireland)</option>
                  <option value="eu-central-1">EU (Frankfurt)</option>
                  <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
                  <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* M365 Configuration */}
        {sourcePaths.some(p => p.startsWith('m365://') || p.startsWith('email://')) && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <span className="text-sm font-medium text-blue-800">Microsoft 365 Credentials Required</span>
              </div>
              <button
                onClick={() => setShowM365Setup(!showM365Setup)}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                {showM365Setup ? 'Hide Setup Guide' : 'Need help? View Setup Guide'}
              </button>
            </div>

            {/* Collapsible Setup Guide */}
            {showM365Setup && (
              <div className="mb-4 p-3 bg-white border border-blue-100 rounded text-xs text-gray-700 space-y-3">
                <h4 className="font-semibold text-blue-800">Azure AD App Registration Setup</h4>

                <div>
                  <p className="font-medium text-gray-800">Step 1: Create App Registration</p>
                  <ol className="list-decimal ml-4 mt-1 space-y-1">
                    <li>Go to <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Azure Portal &gt; App registrations</a></li>
                    <li>Click "New registration"</li>
                    <li>Name: "FOIA Discovery" (or your preferred name)</li>
                    <li>Supported account types: "Single tenant" (recommended)</li>
                    <li>Click "Register"</li>
                  </ol>
                </div>

                <div>
                  <p className="font-medium text-gray-800">Step 2: Get Tenant ID and Client ID</p>
                  <ol className="list-decimal ml-4 mt-1 space-y-1">
                    <li>On the app's Overview page, copy the <strong>Application (client) ID</strong></li>
                    <li>Copy the <strong>Directory (tenant) ID</strong></li>
                  </ol>
                </div>

                <div>
                  <p className="font-medium text-gray-800">Step 3: Create Client Secret</p>
                  <ol className="list-decimal ml-4 mt-1 space-y-1">
                    <li>Go to "Certificates & secrets" in the left menu</li>
                    <li>Click "New client secret"</li>
                    <li>Add a description and select expiration</li>
                    <li>Click "Add" and <strong>immediately copy the Value</strong> (shown only once)</li>
                  </ol>
                </div>

                <div>
                  <p className="font-medium text-gray-800">Step 4: Configure API Permissions</p>
                  <ol className="list-decimal ml-4 mt-1 space-y-1">
                    <li>Go to "API permissions" in the left menu</li>
                    <li>Click "Add a permission" &gt; "Microsoft Graph"</li>
                    <li>Select "Application permissions"</li>
                    <li>Search for and add: <code className="bg-gray-100 px-1">Mail.Read</code></li>
                    <li>Click "Grant admin consent for [your org]" (requires admin)</li>
                  </ol>
                </div>

                <div className="p-2 bg-amber-50 border border-amber-200 rounded">
                  <p className="font-medium text-amber-800">Important Notes:</p>
                  <ul className="list-disc ml-4 mt-1 text-amber-700">
                    <li>Admin consent is required for application permissions</li>
                    <li>The app can read ALL mailboxes in your tenant with Mail.Read</li>
                    <li>For production, consider using Mail.ReadBasic for limited access</li>
                  </ul>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Tenant ID</label>
                <input
                  type="text"
                  value={m365TenantId}
                  onChange={(e) => setM365TenantId(e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client ID</label>
                <input
                  type="text"
                  value={m365ClientId}
                  onChange={(e) => setM365ClientId(e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client Secret</label>
                <input
                  type="password"
                  value={m365ClientSecret}
                  onChange={(e) => setM365ClientSecret(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                />
              </div>
            </div>

            <p className="mt-3 text-xs text-blue-600">
              URI format: <code className="bg-white px-1 rounded">m365://user@domain.com/Inbox</code> or add filters: <code className="bg-white px-1 rounded">m365://user@domain.com/Inbox?from=2024-01-01&subject=keyword</code>
            </p>
          </div>
        )}

        {/* Filters Toggle */}
        <div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <FunnelIcon className="w-4 h-4 mr-1" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>

          {showFilters && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Agency</label>
                <input
                  type="text"
                  value={agency}
                  onChange={(e) => setAgency(e.target.value)}
                  placeholder="e.g. DHS, DOD"
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Requester</label>
                <input
                  type="text"
                  value={requester}
                  onChange={(e) => setRequester(e.target.value)}
                  placeholder="Requester name"
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Relevance Threshold: {relevanceThreshold.toFixed(2)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={relevanceThreshold}
                  onChange={(e) => setRelevanceThreshold(parseFloat(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400">
                  <span>Broad (0.0)</span>
                  <span>Strict (1.0)</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Max File Size (MB)</label>
                <input
                  type="number"
                  value={maxFileSizeMb}
                  onChange={(e) => setMaxFileSizeMb(parseInt(e.target.value) || 50)}
                  min="1"
                  max="500"
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Discover Button */}
        <button
          onClick={handleDiscover}
          disabled={isLoading || !foiaRequestText.trim() || sourcePaths.length === 0}
          className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
              Discovering Documents...
            </>
          ) : (
            <>
              <DocumentMagnifyingGlassIcon className="w-5 h-5 mr-2" />
              Discover Documents
            </>
          )}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Summary Banner */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Discovery Results</h2>
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <ClockIcon className="w-4 h-4" />
                <span>{Number(result.scan_time_seconds || 0).toFixed(1)}s</span>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{result.total_files_scanned}</div>
                <div className="text-xs text-gray-500">Files Scanned</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-700">{result.total_responsive}</div>
                <div className="text-xs text-gray-500">Responsive</div>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <div className="text-2xl font-bold text-amber-700">{result.total_likely_exempt}</div>
                <div className="text-xs text-gray-500">Likely Exempt</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-700">{result.sources_scanned?.length || 0}</div>
                <div className="text-xs text-gray-500">Sources</div>
              </div>
            </div>

            {/* Request Analysis */}
            {result.request_analysis && (
              <div className="border-t border-gray-200 pt-3">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Request Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {result.request_analysis.subject_matter?.length > 0 && (
                    <div>
                      <span className="text-gray-500">Subjects: </span>
                      <span className="text-gray-800">{result.request_analysis.subject_matter.join(', ')}</span>
                    </div>
                  )}
                  {result.request_analysis.keywords?.length > 0 && (
                    <div>
                      <span className="text-gray-500">Keywords: </span>
                      <div className="inline-flex flex-wrap gap-1 mt-0.5">
                        {result.request_analysis.keywords.map((kw, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{kw}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {result.request_analysis.date_range && (
                    <div>
                      <span className="text-gray-500">Date Range: </span>
                      <span className="text-gray-800">
                        {result.request_analysis.date_range.start || '?'} to {result.request_analysis.date_range.end || '?'}
                      </span>
                    </div>
                  )}
                  {result.request_analysis.scope_summary && (
                    <div className="md:col-span-2">
                      <span className="text-gray-500">Scope: </span>
                      <span className="text-gray-800">{result.request_analysis.scope_summary}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Summary */}
            {result.summary && (
              <div className="border-t border-gray-200 pt-3 mt-3">
                <p className="text-sm text-gray-700">{result.summary}</p>
              </div>
            )}
          </div>

          {/* Sort controls */}
          {sortedDocuments.length > 0 && (
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-700">
                {sortedDocuments.length} Document{sortedDocuments.length !== 1 ? 's' : ''}
              </h3>
              <div className="flex items-center space-x-2">
                <ChartBarIcon className="w-4 h-4 text-gray-400" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-xs border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="relevance">Sort by Relevance</option>
                  <option value="name">Sort by Name</option>
                  <option value="size">Sort by Size</option>
                </select>
              </div>
            </div>
          )}

          {/* Document List */}
          <div className="space-y-3">
            {sortedDocuments.map((doc, idx) => {
              const actionBadge = getActionBadge(doc.recommended_action);
              return (
                <div key={idx} className="bg-white rounded-lg border border-gray-200 p-4 hover:border-gray-300 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* File header */}
                      <div className="flex items-center space-x-2 mb-1">
                        <DocumentTextIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <h4 className="text-sm font-medium text-gray-900 truncate">{doc.filename}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRelevanceColor(doc.relevance_score)}`}>
                          {(doc.relevance_score * 100).toFixed(0)}%
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${actionBadge.color}`}>
                          {actionBadge.label}
                        </span>
                      </div>

                      {/* File meta */}
                      <div className="flex items-center space-x-3 text-xs text-gray-400 mb-2">
                        <span>{doc.file_type}</span>
                        <span>{formatFileSize(doc.file_size)}</span>
                        {doc.last_modified && <span>{new Date(doc.last_modified).toLocaleDateString()}</span>}
                        <span className="font-mono text-gray-300 truncate max-w-xs">{doc.file_path}</span>
                      </div>

                      {/* Relevance reasoning */}
                      <p className="text-sm text-gray-600 mb-2">{doc.relevance_reasoning}</p>

                      {/* Matched keywords */}
                      {doc.matched_keywords?.length > 0 && (
                        <div className="flex items-center flex-wrap gap-1 mb-2">
                          <span className="text-xs text-gray-400">Matched:</span>
                          {doc.matched_keywords.map((kw, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded text-xs">{kw}</span>
                          ))}
                        </div>
                      )}

                      {/* Exemptions */}
                      {doc.exemptions_likely?.length > 0 && (
                        <div className="flex items-center flex-wrap gap-1 mb-2">
                          <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-500" />
                          <span className="text-xs text-amber-600">Likely Exemptions:</span>
                          {doc.exemptions_likely.map((ex, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium">{ex}</span>
                          ))}
                        </div>
                      )}

                      {/* Content preview */}
                      {doc.content_preview && (
                        <details className="mt-2">
                          <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                            Preview content
                          </summary>
                          <pre className="mt-1 p-2 bg-gray-50 rounded text-xs text-gray-600 whitespace-pre-wrap max-h-40 overflow-y-auto">
                            {doc.content_preview}
                          </pre>
                        </details>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="ml-4 flex-shrink-0">
                      <button
                        onClick={() => sendToRedaction(doc)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700"
                      >
                        FOIA Redaction
                        <ArrowRightIcon className="w-3 h-3 ml-1" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {sortedDocuments.length === 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <DocumentMagnifyingGlassIcon className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">No responsive documents found above the relevance threshold.</p>
              <p className="text-xs text-gray-400 mt-1">Try lowering the relevance threshold or adding more data sources.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
